import { api } from "./api";

const SW_PATH = "/sw.js";

export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function isIos() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const isiPad =
        /iPad|Macintosh/i.test(ua) && "ontouchend" in window;
    return /iPhone|iPod/i.test(ua) || isiPad;
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function urlBase64ToUint8Array(b64) {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

async function ensureSwRegistered() {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_PATH);
}

export async function getCurrentPermission() {
    if (!isPushSupported()) return "unsupported";
    return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function subscribeToPush() {
    if (!isPushSupported()) throw new Error("unsupported");
    if (isIos() && !isStandalone()) {
        throw new Error("ios-needs-install");
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("denied");

    const reg = await ensureSwRegistered();
    const { data } = await api.get("/push/vapid-key");
    const key = urlBase64ToUint8Array(data.public_key);

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: key,
        });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: {
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
        },
    });
    return sub;
}

export async function unsubscribeFromPush() {
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try {
        await api.post("/push/unsubscribe", { endpoint });
    } catch {
        /* ignore */
    }
}

export async function getExistingSubscription() {
    if (!isPushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
}
